import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Checkbox, Dropdown, Input, Modal } from 'antd';
import { useTypedSelector } from '@hooks';
import { TranscribeAssetDto } from '@api/Api';
import { transcribeAssetVersion } from '@redux/actions/assetsAction';
import { transcribeLanguages } from '@helpers/transcribeLanguages';
import { useResponsive } from '@hooks/useResponsive';

import { ReactComponent as DownArrowSvg } from '@assets/icons/arrow-down.svg';
import { ReactComponent as RightArrowSvg } from '@assets/icons/arrow-right.svg';
import { ReactComponent as GenerateTranscriptionSvg } from '@assets/icons/MediaViewer/generate-transcription.svg';
import { ReactComponent as SearchSvg } from '@assets/icons/search.svg';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';

import './CreateTranscript.less';

const CONFIRMED_APPLY_SPEAKER_KEY = 'transcription_consent_accepted';

export default function CreateTranscript() {
  const dispatch = useDispatch();
  const asset = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const { isMdWidth } = useResponsive();
  const actionsRef = useRef<HTMLDivElement>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en-US');
  const [searchTextLanguage, setSearchTextLanguage] = useState('');
  const [isSpeakerLabels, setIsSpeakerLabels] = useState(false);
  const [isConfirmedApplySpeaker, setIsConfirmedApplySpeaker] = useState(false);
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const options = useMemo(() => {
    let _items = transcribeLanguages;
    if (searchTextLanguage) {
      _items = _items.filter((language) =>
        language.label.toLowerCase().includes(searchTextLanguage.toLowerCase())
      );
    }
    return _items;
  }, [searchTextLanguage]);

  const transcribeLanguagesLabel = useMemo(() => {
    return (
      transcribeLanguages.find((language) => language.key === selectedLanguage)
        ?.label || selectedLanguage
    );
  }, [selectedLanguage]);

  const onCreateTranscript = (force = false) => {
    if (!asset) return;
    if (!selectedVersion) return;
    const isConfirmed =
      localStorage.getItem(CONFIRMED_APPLY_SPEAKER_KEY) === 'true';
    if (!force && isSpeakerLabels && !isConfirmed) {
      setIsOpenConfirm(true);
      return;
    }
    setIsOpenConfirm(false);
    if (isConfirmedApplySpeaker) {
      localStorage.setItem(CONFIRMED_APPLY_SPEAKER_KEY, 'true');
    }
    dispatch(
      transcribeAssetVersion({
        assetId: asset.id,
        assetVersionId: selectedVersion.id,
        language: [selectedLanguage] as TranscribeAssetDto['language'],
        speakerLabels: isSpeakerLabels
      })
    );
  };

  useEffect(() => {
    const browserLocale =
      navigator.languages && navigator.languages.length > 0
        ? navigator.languages[0]
        : navigator.language;
    const existingLanguage = transcribeLanguages.find(
      (language) => language.key === browserLocale
    );
    if (existingLanguage) {
      setSelectedLanguage(existingLanguage.key);
    }
  }, []);

  return (
    <div className="media_viewer_create-transcript">
      <div className="media_viewer_create-transcript-icon">
        <GenerateTranscriptionSvg />
      </div>
      <div className="media_viewer_create-transcript-title">
        Generate transcription
      </div>
      <div className="media_viewer_create-transcript-text">
        Create a transcript and captions for your asset to make navigation and
        feedback easier.
      </div>
      <div ref={actionsRef} className="media_viewer_create-transcript-actions">
        <Dropdown
          getPopupContainer={() => actionsRef.current || document.body}
          menu={{
            selectable: true,
            items: [
              {
                key: 'languageGroupHead',
                label: (
                  <Input
                    prefix={<SearchSvg />}
                    placeholder="Search"
                    value={searchTextLanguage}
                    className="media_viewer_create-transcript-language-search"
                    onChange={(e) => setSearchTextLanguage(e.target.value)}
                  />
                ),
                type: 'group'
              },
              {
                key: 'languageGroup',
                type: 'group',
                className: 'media_viewer_create-transcript-language-menu',
                children: [
                  ...options,
                  ...(isMdWidth
                    ? [
                        {
                          key: 'cancel',
                          label: 'Cancel'
                        }
                      ]
                    : [])
                ]
              }
            ],
            selectedKeys: [selectedLanguage],
            onSelect: ({ key }) => {
              if (key.indexOf('cancel') < 0) {
                setSelectedLanguage(key);
              }
            }
          }}
          prefixCls="media_viewer_menu"
          overlayClassName="media_viewer_create-transcript-menu media_viewer_mobile-overlay"
          placement="bottomLeft"
          trigger={['click']}
        >
          <Button
            className="media_viewer_btn media_viewer_create-transcript-language-btn"
            icon={<DownArrowSvg />}
          >
            {transcribeLanguagesLabel}
          </Button>
        </Dropdown>
        <Button
          size="small"
          type="primary"
          icon={<RightArrowSvg width={24} height={24} />}
          className="media_viewer_create-transcript-transcribe-btn"
          onClick={() => onCreateTranscript()}
        >
          Transcribe
        </Button>
      </div>
      <Checkbox
        onChange={(e) => {
          setIsSpeakerLabels(e.target.checked);
        }}
        checked={isSpeakerLabels}
        className="media_viewer_create-transcript-speaker-labels-checkbox"
      >
        Identify speakers
      </Checkbox>
      <Modal
        className="speaker-identification-modal"
        open={isOpenConfirm}
        footer={null}
        destroyOnClose
        centered
        width={512}
        closeIcon={<CrossSvg />}
        onCancel={() => setIsOpenConfirm(false)}
      >
        <div className="modal_container">
          <h2 className="modal_container__title">Speaker identification</h2>
          <div className="modal_container__subtitle">
            <p>
              StreamWork can analyze the audio in this file to detect different
              voices and automatically group the transcript by speaker (e.g.
              Speaker 1, Speaker 2).
            </p>
            <p>
              By enabling this option, you confirm that all individuals whose
              voices appear in the recording have agreed to this processing.
            </p>
            <p>
              This feature may be limited or unavailable in certain regions.
            </p>
          </div>
          <Checkbox
            onChange={(e) => {
              setIsConfirmedApplySpeaker(e.target.checked);
            }}
            checked={isConfirmedApplySpeaker}
            className="media_viewer_create-transcript-speaker-labels-checkbox"
          >
            Apply speaker identification to future transcriptions
          </Checkbox>
          <div className="modal_container__actions">
            <Button
              type="text"
              style={{
                marginRight: '16px',
                border: 'none',
                height: 48,
                padding: '8px 32px'
              }}
              onClick={() => setIsOpenConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={() => onCreateTranscript(true)}
              style={{
                width: 188,
                height: 48,
                padding: '8px 15px'
              }}
            >
              I Agree
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
